import Link from 'components/Basic/Link/Link';
import AddToCart from 'components/Blocks/Product/AddToCart/AddToCart';
import { AddToConfigurator } from 'components/Blocks/Product/AddToCart/AddToConfigurator';
import { SpinboxAddToCart } from 'components/Blocks/Product/AddToCart/SpinboxAddToCart';
import WatchDog from 'components/Blocks/Product/WatchDog/WatchDog';
import Button from 'components/Forms/Button/Button';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/dist/client/router';
import { ExtendedConfiguratorProductApi } from 'types/configurator';
import { GtmListNameType } from 'types/gtm';
import { ProductActionType } from 'types/product';
import { onClickProductDetailGtmEventHandler } from 'utils/Gtm/EventHandlers';

type ProductActionProps = {
    idSuffix?: string;
    product: ProductActionType;
    gtmListName: GtmListNameType;
    listIndex: number;
    isConfigurator?: boolean;
    onAddToCart?: (productUuid: string) => void;
};

const ProductAction: FC<ProductActionProps> = ({
    idSuffix,
    gtmListName,
    listIndex,
    product,
    isConfigurator,
    onAddToCart,
}) => {
    const testIdentifier = 'blocks-product-action';

    const router = useRouter();
    const { t } = useTranslation();
    const { type: domainType, url } = useDomainConfig();

    const productButtonType = (() => {
        if (isConfigurator) {
            return 'configurator' as const;
        }
        if (domainType === 'web') {
            return 'web' as const;
        }
        if (product.isMainVariant) {
            return 'choose-color' as const;
        }
        if (domainType === 'b2b') {
            return 'b2b' as const;
        }
        if (product.isSellingDenied) {
            return 'watch-dog' as const;
        }

        return 'add-to-cart' as const;
    })();
    const isB2bAddToCartDisabled =
        product.isSellingDenied ||
        product.maximumOrderableQuantity <= 0 ||
        (product.b2bMinimumProductOrder ?? 0) > product.maximumOrderableQuantity;

    return (
        <span className="mt-5" data-testid={testIdentifier}>
            {productButtonType === 'web' && (
                <Link
                    isButton
                    href={product.slug}
                    size="smallNarrow"
                    title={`${t('Show product details')} ${product.fullName}`}
                    onClick={() => onClickProductDetailGtmEventHandler(product, gtmListName, listIndex, url)}
                >
                    {t('Show')}
                </Link>
            )}
            {productButtonType === 'add-to-cart' && (
                <AddToCart
                    gtmListName={gtmListName}
                    isBookingOnly={product.maximumOrderableQuantity <= 0}
                    listIndex={listIndex}
                    productUuid={product.uuid}
                    quantity={product.b2bMinimumProductOrder ?? 1}
                    onAddToCart={onAddToCart}
                />
            )}
            {productButtonType === 'choose-color' && (
                <Button
                    aria-label={`${t('Choose color of product')} ${product.fullName}`}
                    data-testid={`${testIdentifier}-${productButtonType}`}
                    name={productButtonType}
                    size="smallNarrow"
                    onClick={() => router.push(product.slug)}
                >
                    {t('Choose color')}
                </Button>
            )}
            {productButtonType === 'watch-dog' && (
                <WatchDog productName={product.fullName} productUuid={product.uuid} />
            )}
            {productButtonType === 'configurator' && (
                // TODO: Refactor ProductAction call
                // eslint-disable-next-line
                // @ts-ignore: TS2352 - Suppress type assertion error
                <AddToConfigurator product={product as ExtendedConfiguratorProductApi} />
            )}
            {productButtonType === 'b2b' && (
                <>
                    {isB2bAddToCartDisabled ? (
                        <WatchDog productName={product.fullName} productUuid={product.uuid} />
                    ) : (
                        <SpinboxAddToCart
                            gtmListName={gtmListName}
                            listIndex={listIndex}
                            product={product}
                            spinboxIdSuffix={idSuffix}
                            variant="iconOnly"
                        />
                    )}
                </>
            )}
        </span>
    );
};

export default ProductAction;
